import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React from 'react';
import PeriversLogoDark from '../../../../assets/svg/perivers-logo-dark.svg';
import { BottomNavigationItems, Navigation } from '../navigation/navigation';
import {
  dialogPaperSx,
  dialogRootSx,
  dialogTitleSx,
  dialogTitleTextSx,
} from './styles';
import { useCloseOnResize } from './hooks';
import { useAuthContext } from '../../../auth';
import { UserProfileDropdown } from '../user-profile';

interface MobileNavigationDialogProps {
  open: boolean;
  onClose: () => void;
  userDisplayName?: string;
  userInitials?: string;
}

export const MobileNavigationDialog: React.FC<MobileNavigationDialogProps> = ({
  open,
  onClose,
  userDisplayName,
  userInitials,
}) => {
  const { user } = useAuthContext();
  const resolvedDisplayName =
    userDisplayName ?? user?.username ?? 'User';
  const resolvedInitials =
    userInitials ?? resolvedDisplayName.substring(0, 2).toUpperCase();
  const handleNavigationClick = () => {
    onClose();
  };

  useCloseOnResize({ open, onClose });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Use fullScreen for true edge-to-edge coverage and to bypass default padding
      fullScreen
      // Use slotProps to apply custom styling to the internal Paper element
      slotProps={{
        paper: {
          sx: dialogPaperSx,
        },
      }}
      sx={dialogRootSx}
    >
      {/* 1. Dialog Header (Logo/Title and Close Button) */}
      <DialogTitle sx={dialogTitleSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box
            component="img"
            src={PeriversLogoDark}
            alt="Peritas"
            sx={{ width: '18px', height: '27px', display: 'block', flexShrink: 0, marginTop:"-5px" }}
          />
          <Typography
            variant="h6"
            sx={{
              ...dialogTitleTextSx,
              color: '#263238',
              fontFamily: '"Helvetica Neue"',
              fontSize: '24px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '150%',
              letterSpacing: '0.15px',
              fontFeatureSettings: "'liga' off, 'clig' off",
              marginTop:"-3px"
            }}
          >
            Peritas
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          autoFocus
          sx={{ color: 'text-gray-500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* 2. Dialog Content (Navigation and User Profile) */}
      <DialogContent
        sx={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Main Navigation */}
          <div className="flex-1 pt-[16px] pb-[16px] pl-[8px] pr-[8px]">
            <Navigation
              isCollapsed={false}
              onNavigationClick={handleNavigationClick}
              isMobileDialog={true}
            />
            {/* Bottom Navigation Items and Divider */}
            <div className="border-t border-gray-200 p-0 mt-[8px] pt-[8px]">
              <BottomNavigationItems
                isCollapsed={false}
                onNavigationClick={handleNavigationClick}
                isMobileDialog={true}
              />
            </div>
          </div>

          

          {/* 3. User Profile at the very bottom */}
          <div className="p-4 border-t border-gray-200">
            <UserProfileDropdown
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
